import { getApp, getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, initializeFirestore } from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";

export {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  signOut,
} from "firebase/auth";
export {
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, setDoc,
  onSnapshot, query, where, orderBy, limit, serverTimestamp, arrayUnion,
  arrayRemove, writeBatch,
} from "firebase/firestore";
export {
  ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject,
} from "firebase/storage";

const useLocalEmulators = import.meta.env.VITE_ALPHA_USE_EMULATORS === "true";
const productionProjectId = "alphaportal-3e1d8";
const emulatorProjectId = String(import.meta.env.VITE_ALPHA_EMULATOR_PROJECT_ID || "").trim();
const emulatorHosts = {
  auth: String(import.meta.env.VITE_ALPHA_AUTH_EMULATOR_HOST || "").trim(),
  firestore: String(import.meta.env.VITE_ALPHA_FIRESTORE_EMULATOR_HOST || "").trim(),
  storage: String(import.meta.env.VITE_ALPHA_STORAGE_EMULATOR_HOST || "").trim(),
};

function parseLoopbackHost(value, label) {
  if (!value) throw new Error(`[Alpha emulator safety] Missing ${label}.`);
  const url = new URL(value.includes("://") ? value : `http://${value}`);
  if (!["127.0.0.1", "localhost", "::1"].includes(url.hostname)) {
    throw new Error(`[Alpha emulator safety] ${label} must use a loopback host.`);
  }
  const port = Number(url.port);
  if (!Number.isInteger(port) || port < 1) throw new Error(`[Alpha emulator safety] ${label} must include a port.`);
  return { hostname: url.hostname, port, origin: url.origin };
}

let parsedEmulatorHosts = null;
if (useLocalEmulators) {
  if (!emulatorProjectId) throw new Error("[Alpha emulator safety] Missing VITE_ALPHA_EMULATOR_PROJECT_ID.");
  if (emulatorProjectId === productionProjectId) throw new Error("[Alpha emulator safety] Production project ID is forbidden in emulator mode.");
  if (emulatorProjectId !== "alpha-school-rules-test") throw new Error(`[Alpha emulator safety] Unexpected emulator project: ${emulatorProjectId}.`);
  parsedEmulatorHosts = {
    auth: parseLoopbackHost(emulatorHosts.auth, "VITE_ALPHA_AUTH_EMULATOR_HOST"),
    firestore: parseLoopbackHost(emulatorHosts.firestore, "VITE_ALPHA_FIRESTORE_EMULATOR_HOST"),
    storage: parseLoopbackHost(emulatorHosts.storage, "VITE_ALPHA_STORAGE_EMULATOR_HOST"),
  };
}

const alphaFirebaseConfig = {
  apiKey: "AIzaSyB7Z7n04HtSikkfrn4TWmAseMk_slwp25I",
  authDomain: "alphaportal-3e1d8.firebaseapp.com",
  projectId: useLocalEmulators ? emulatorProjectId : productionProjectId,
  storageBucket: "alphaportal-3e1d8.firebasestorage.app",
  messagingSenderId: "10741436310",
  appId: "1:10741436310:web:119317ce5c6aaa89ed97b7",
  measurementId: "G-WLGYDM2E5E",
};

const alphaApp = getApps().find((app) => app.name === "alpha-school-portal")
  || initializeApp(alphaFirebaseConfig, "alpha-school-portal");
const alphaAuth = getAuth(alphaApp);
let alphaDb;
try {
  alphaDb = initializeFirestore(alphaApp, {
    ignoreUndefinedProperties: true,
    experimentalForceLongPolling: true,
  });
} catch {
  alphaDb = getFirestore(getApp("alpha-school-portal"));
}
const alphaStorage = getStorage(alphaApp);
if (useLocalEmulators && !globalThis.__alphaFirebaseEmulatorsConnected) {
  connectAuthEmulator(alphaAuth, parsedEmulatorHosts.auth.origin, { disableWarnings: true });
  connectFirestoreEmulator(alphaDb, parsedEmulatorHosts.firestore.hostname, parsedEmulatorHosts.firestore.port);
  connectStorageEmulator(alphaStorage, parsedEmulatorHosts.storage.hostname, parsedEmulatorHosts.storage.port);
  globalThis.__alphaFirebaseEmulatorsConnected = true;
}

export const getFirebaseApp = () => alphaApp;
export const getFirebaseAuth = () => alphaAuth;
export const getFirebaseDb = () => alphaDb;
export const storage = alphaStorage;
